import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridSortModel, GridColDef } from '@mui/x-data-grid';
import { DataGridPro, GridTreeNodeWithRender } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';
import TableToolbar from './TableToolbar';
import { styled } from 'styled-components';
import { Typography } from '@mui/material';

interface TableProps {
	title?: string;
	columns: GridColDef[];
	rows: any[];
	rowCount: number;
	loading?: boolean;
	// processRowUpdate?: (row: any) => void;
	initialState?: any;
	sortModel: GridSortModel;
	onSortModelChange: (model: GridSortModel) => void;
	page: number;
	pageSize: number;
	onPaginationModelChange: (paginationModel: any) => void;
	handleTextSearch?: (searchText: string) => void;
	searchText?: string;
	// setSearchText?: (searchText: string) => void;
	checkboxSelection?: boolean;
	rowHeight?: number;
	pagination?: boolean;
	paginationMode?: 'client' | 'server';
	toolbar?: any;
	hideFooterPagination?: boolean;
	boxSx?: any;
	autoHeight?: boolean;
	hideFooter?: boolean;
	handleAdd?: () => void;
	filterOptions?: TableFilterOption[];
	noRowsContent?: React.ReactNode;
	headerButtons?: React.ReactNode[];
	showSimpleRowCount?: boolean;
	// Tree data props
	treeData?: boolean;
	getTreeDataPath?: (row: any) => any[];
	groupingColDef?: any;
	defaultGroupingExpansionDepth?: number;
	getRowClassName?: (params: any) => string;
	// Custom tree icons
	treeExpandIcon?: React.ComponentType<any>;
	treeCollapseIcon?: React.ComponentType<any>;
	// Selection and bulk actions
	selectionModel?: any[];
	onSelectionModelChange?: (newSelectionModel: any[]) => void;
	bulkActions?: React.ReactNode;
	// Column visibility props
	columnVisibilityModel?: Record<string, boolean>;
	onColumnVisibilityModelChange?: (model: Record<string, boolean>) => void;
	enableColumnVisibilityToggle?: boolean;
	// Row selection behavior
	disableRowSelectionOnClick?: boolean;
}

export interface TableFilterOption {
	field: string;
	label: string;
	value: string;
	options: any[];
	onChange: any;
}

const StyledNoRowsOverlay = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	marginTop: '25px',
	height: '100%',
	padding: '2rem',
	fontSize: '16px',
	fontWeight: 500,
});

const StyledNoRowsOverlayText = styled('div')({
	textAlign: 'center',
	maxWidth: '600px',
});

function NoRowsOverlay({ content }: { content?: React.ReactNode }) {
	return (
		<StyledNoRowsOverlay>
			<StyledNoRowsOverlayText>
				{content || 'No rows'}
			</StyledNoRowsOverlayText>
		</StyledNoRowsOverlay>
	);
}

const StyledFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	padding: 10px 16px;
	border-top: 1px solid rgba(49, 49, 52, 1);
	background-color: ${(props) => props.theme.palette.background.paper};
`;

function SimpleRowCountFooter({ rowCount }: { rowCount: number }) {
	return (
		<StyledFooter>
			<Typography variant="body2" color="text.secondary">
				{rowCount} {rowCount === 1 ? 'row' : 'rows'}
			</Typography>
		</StyledFooter>
	);
}

export default function Table(props: TableProps) {
	const {
		title,
		columns,
		rows,
		rowCount,
		loading = false,
		// processRowUpdate,
		initialState,
		sortModel,
		onSortModelChange,
		page,
		pageSize,
		onPaginationModelChange,
		checkboxSelection,
		rowHeight = 52,
		pagination,
		paginationMode = 'server',
		handleTextSearch,
		searchText,
		hideFooterPagination,
		toolbar,
		boxSx = {},
		autoHeight = false,
		hideFooter = false,
		handleAdd,
		filterOptions = [],
		noRowsContent,
		headerButtons,
		showSimpleRowCount = false,
		// Tree data props
		treeData = false,
		getTreeDataPath,
		groupingColDef,
		defaultGroupingExpansionDepth,
		getRowClassName,
		treeExpandIcon,
		treeCollapseIcon,
		// Selection and bulk actions
		selectionModel = [],
		onSelectionModelChange,
		bulkActions,
		// Column visibility props
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		enableColumnVisibilityToggle = false,
		// Row selection behavior
		disableRowSelectionOnClick = false,
	} = props;

	const theme = useTheme();

	// Use DataGridPro if treeData is true, otherwise use regular DataGrid
	const GridComponent = treeData ? DataGridPro : DataGrid;

	// Create custom tree expand/collapse icon components for slots
	const customTreeDataExpandIcon = React.useMemo(() => {
		if (!treeExpandIcon) return undefined;
		const ExpandIcon = treeExpandIcon;
		return (props: any) => (
			<ExpandIcon {...props} fontSize="small" color="primary" />
		);
	}, [treeExpandIcon]);

	const customTreeDataCollapseIcon = React.useMemo(() => {
		if (!treeCollapseIcon) return undefined;
		const CollapseIcon = treeCollapseIcon;
		return (props: any) => (
			<CollapseIcon {...props} fontSize="small" color="primary" />
		);
	}, [treeCollapseIcon]);

	return (
		<Box
			sx={{
				width: '100%',
				backgroundColor: theme.palette.background.paper,
				borderRadius: 2,
				position: 'relative',
				flex: 1,
				overflow: 'auto',
				height: 'calc(100vh - 98px)',
				...boxSx,
			}}
		>
			<GridComponent
				sx={{
					'& .MuiDataGrid-columnHeader': {
						backgroundColor: theme.palette.background.paper,
					},
					'& .MuiDataGrid-filler': {
						backgroundColor: theme.palette.background.paper,
					},
					'& .MuiDataGrid-treeDataGroupingCell': {
						// Style the grouping cells
						fontWeight: 500,
					},
					'& .MuiDataGrid-treeDataGroupingCellToggle': {
						// Style the toggle button to match the mockup
						color: theme.palette.primary.main,
					},
					'& .MuiDataGrid-row--group': {
						// Style group rows
						backgroundColor:
							theme.palette.mode === 'dark'
								? 'rgba(71, 98, 130, 0.1)'
								: 'rgba(232, 245, 254, 0.4)',
					},
					// Style child rows to be slightly indented and different from parents
					'& .MuiDataGrid-row.row-is-child': {
						backgroundColor:
							theme.palette.mode === 'dark'
								? 'rgba(71, 98, 130, 0.05)'
								: 'rgba(232, 245, 254, 0.2)',
					},
					borderColor: 'transparent',
					borderRadius: 2,
					// Set custom indentation for tree data
					'--DataGrid-cellOffsetMultiplier': 2,
				}}
				loading={loading}
				rows={rows}
				rowCount={rowCount}
				hideFooterPagination={hideFooterPagination}
				paginationMode={paginationMode}
				slots={{
					toolbar: () =>
						toolbar ? (
							toolbar
						) : (
							<TableToolbar
								title={title}
								searchText={searchText}
								handleTextSearch={handleTextSearch}
								handleAdd={handleAdd}
								filterOptions={filterOptions}
								headerButtons={headerButtons}
								bulkActions={bulkActions}
								selectedCount={selectionModel?.length || 0}
								columns={columns}
								columnVisibilityModel={columnVisibilityModel}
								onColumnVisibilityModelChange={
									onColumnVisibilityModelChange
								}
								enableColumnVisibilityToggle={
									enableColumnVisibilityToggle
								}
							/>
						),
					noRowsOverlay: () => (
						<NoRowsOverlay content={noRowsContent} />
					),
					...(showSimpleRowCount && {
						footer: () => (
							<SimpleRowCountFooter rowCount={rowCount} />
						),
					}),
					...(customTreeDataExpandIcon && {
						treeDataExpandIcon: customTreeDataExpandIcon,
					}),
					...(customTreeDataCollapseIcon && {
						treeDataCollapseIcon: customTreeDataCollapseIcon,
					}),
				}}
				{...(pagination && {
					pagination: true,
					paginationModel: { page, pageSize },
					onPaginationModelChange: (paginationModel: any) => {
						onPaginationModelChange(paginationModel);
					},
					pageSizeOptions: [],
				})}
				columns={columns}
				sortModel={sortModel}
				keepNonExistentRowsSelected
				onSortModelChange={onSortModelChange}
				checkboxSelection={checkboxSelection}
				disableRowSelectionOnClick={disableRowSelectionOnClick}
				// editMode="row"
				initialState={initialState}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={onColumnVisibilityModelChange}
				rowSelectionModel={selectionModel}
				onRowSelectionModelChange={(newSelectionModel) => {
					if (onSelectionModelChange) {
						onSelectionModelChange([...newSelectionModel]);
					}
				}}
				hideFooterSelectedRowCount
				rowHeight={rowHeight}
				autoHeight={autoHeight}
				hideFooter={showSimpleRowCount ? false : hideFooter}
				// Tree data props
				treeData={treeData}
				getTreeDataPath={getTreeDataPath}
				groupingColDef={groupingColDef}
				defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
				getRowClassName={getRowClassName}
			/>
		</Box>
	);
}

export function SimpleTable(props: TableProps) {
	const {
		title,
		columns,
		rows,
		rowCount,
		loading = false,
		// processRowUpdate,
		initialState,
		sortModel,
		onSortModelChange,
		page,
		pageSize,
		onPaginationModelChange,
		checkboxSelection,
		rowHeight = 52,
		pagination,
		paginationMode = 'server',
		handleTextSearch,
		searchText,
		hideFooterPagination,
		toolbar,
		boxSx = {},
		autoHeight = false,
		hideFooter = false,
		handleAdd,
		filterOptions = [],
		noRowsContent,
		headerButtons,
		showSimpleRowCount = false,
		// Tree data props
		treeData = false,
		getTreeDataPath,
		groupingColDef,
		defaultGroupingExpansionDepth,
		getRowClassName,
		treeExpandIcon,
		treeCollapseIcon,
		// Selection and bulk actions
		selectionModel = [],
		onSelectionModelChange,
		bulkActions,
		// Column visibility props
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		enableColumnVisibilityToggle = false,
		// Row selection behavior
		disableRowSelectionOnClick = false,
	} = props;

	const theme = useTheme();

	// Use DataGridPro if treeData is true, otherwise use regular DataGrid
	const GridComponent = treeData ? DataGridPro : DataGrid;

	// Create custom tree expand/collapse icon components for slots
	const customTreeDataExpandIcon = React.useMemo(() => {
		if (!treeExpandIcon) return undefined;
		const ExpandIcon = treeExpandIcon;
		return (props: any) => (
			<ExpandIcon {...props} fontSize="small" color="primary" />
		);
	}, [treeExpandIcon]);

	const customTreeDataCollapseIcon = React.useMemo(() => {
		if (!treeCollapseIcon) return undefined;
		const CollapseIcon = treeCollapseIcon;
		return (props: any) => (
			<CollapseIcon {...props} fontSize="small" color="primary" />
		);
	}, [treeCollapseIcon]);

	return (
		<GridComponent
			sx={{
				'& .MuiDataGrid-columnHeader': {
					backgroundColor: theme.palette.background.paper,
				},
				'& .MuiDataGrid-filler': {
					backgroundColor: theme.palette.background.paper,
				},
				'& .MuiDataGrid-treeDataGroupingCell': {
					// Style the grouping cells
					fontWeight: 500,
				},
				'& .MuiDataGrid-treeDataGroupingCellToggle': {
					// Style the toggle button to match the mockup
					color: theme.palette.primary.main,
				},
				'& .MuiDataGrid-row--group': {
					// Style group rows
					backgroundColor:
						theme.palette.mode === 'dark'
							? 'rgba(71, 98, 130, 0.1)'
							: 'rgba(232, 245, 254, 0.4)',
				},
				// Style child rows to be slightly indented and different from parents
				'& .MuiDataGrid-row.row-is-child': {
					backgroundColor:
						theme.palette.mode === 'dark'
							? 'rgba(71, 98, 130, 0.05)'
							: 'rgba(232, 245, 254, 0.2)',
				},
				borderColor: 'transparent',
				borderRadius: 2,
				// Set custom indentation for tree data
				'--DataGrid-cellOffsetMultiplier': 2,
			}}
			loading={loading}
			rows={rows}
			rowCount={rowCount}
			hideFooterPagination={hideFooterPagination}
			paginationMode={paginationMode}
			slots={{
				toolbar: () =>
					toolbar ? (
						toolbar
					) : (
						<TableToolbar
							title={title}
							searchText={searchText}
							handleTextSearch={handleTextSearch}
							handleAdd={handleAdd}
							filterOptions={filterOptions}
							headerButtons={headerButtons}
							bulkActions={bulkActions}
							selectedCount={selectionModel?.length || 0}
							columns={columns}
							columnVisibilityModel={columnVisibilityModel}
							onColumnVisibilityModelChange={
								onColumnVisibilityModelChange
							}
							enableColumnVisibilityToggle={
								enableColumnVisibilityToggle
							}
						/>
					),
				noRowsOverlay: () => <NoRowsOverlay content={noRowsContent} />,
				...(showSimpleRowCount && {
					footer: () => <SimpleRowCountFooter rowCount={rowCount} />,
				}),
				...(customTreeDataExpandIcon && {
					treeDataExpandIcon: customTreeDataExpandIcon,
				}),
				...(customTreeDataCollapseIcon && {
					treeDataCollapseIcon: customTreeDataCollapseIcon,
				}),
			}}
			{...(pagination && {
				pagination: true,
				paginationModel: { page, pageSize },
				onPaginationModelChange: (paginationModel: any) => {
					onPaginationModelChange(paginationModel);
				},
				pageSizeOptions: [],
			})}
			columns={columns}
			sortModel={sortModel}
			keepNonExistentRowsSelected
			onSortModelChange={onSortModelChange}
			checkboxSelection={checkboxSelection}
			disableRowSelectionOnClick={disableRowSelectionOnClick}
			initialState={initialState}
			columnVisibilityModel={columnVisibilityModel}
			onColumnVisibilityModelChange={onColumnVisibilityModelChange}
			rowSelectionModel={selectionModel}
			onRowSelectionModelChange={(newSelectionModel) => {
				if (onSelectionModelChange) {
					onSelectionModelChange([...newSelectionModel]);
				}
			}}
			hideFooterSelectedRowCount
			rowHeight={rowHeight}
			autoHeight={autoHeight}
			hideFooter={showSimpleRowCount ? false : hideFooter}
			// Tree data props
			treeData={treeData}
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
			getRowClassName={getRowClassName}
		/>
	);
}

export const TableHeader = styled.div`
	color: ${(props) => props.theme.palette.text.label};
	margin-left: 10px;
	margin-top: 10px;
	margin-bottom: 10px;
	display: flex;
	align-items: center;
	font-weight: normal;
	font-size: 24px;
`;
